import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Box,
  Divider,
  Snackbar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { apiService } from "../components/ReasuableFuncations/api";

export default function IctStatDashboard() {
  const [ictStatistics, setIctStatistics] = useState([]);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedStat, setSelectedStat] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [statToDelete, setStatToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [editFormData, setEditFormData] = useState({
    baseline: "",
    target: "",
    targetAchieved: "",
    cumulativeTarget: "",
  });
  const [userRole, setUserRole] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await apiService.get('/api/ict-statistics');
        if (response.statusText !== "OK") {
          throw new Error("Failed to fetch statistics");
        }
        setIctStatistics(response.data);
      } catch (error) {
        console.error("Error fetching statistics:", error);
        setError("Failed to fetch statistics. Please try again later.");
      }
    };

    fetchStats();
  }, []);

  useEffect(() => {
    // Scroll to the top of the page when the component mounts
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user'));
    if (userData) {
      const { selectedOption, regionalNetworkOption, memberStateOption, countryOption } = userData;
      setUserRole({ selectedOption, regionalNetworkOption, memberStateOption, countryOption });
    }
  }, []);

  const canEditDelete = (stat) => {
    if (!userRole) return false;
    
    switch (userRole.selectedOption) {
      case 'Comesa':
        return true; // Comesa users can edit/delete all stats
      case 'Regional Network':
        return stat.regionalNetworkOption === userRole.regionalNetworkOption;
      case 'Member State':
        return stat.countryOption === userRole.countryOption;
      default:
        return false;
    }
  };

  const handleEdit = (stat) => {
    setEditFormData({
      baseline: stat.baseline,
      target: stat.target,
      targetAchieved: stat.targetAchieved,
      cumulativeTarget: stat.cumulativeTarget,
    });
    setSelectedStat(stat);
    setOpenDialog(true);
  };

  const handleUpdate = async () => {
    try {
      const response = await apiService.put(
        `/api/ict-statistics/${selectedStat._id}`,
        editFormData
      );

      if (response.statusText === "OK") {
        setIctStatistics((stats) =>
          stats.map((s) =>
            s._id === selectedStat._id ? { ...s, ...editFormData } : s
          )
        );
        setSnackbar({
          open: true,
          message: "ICT Statistics updated successfully!",
          severity: "success",
        });
        setOpenDialog(false);
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Error updating ICT Statistics",
        severity: "error",
      });
    }
  };

  const handleDelete = async () => {
    if (statToDelete) {
      try {
        const response = await apiService.delete(
          `/api/ict-statistics/${statToDelete}`
        );
        if (response.statusText === "OK") {
          setIctStatistics((stats) =>
            stats.filter((s) => s._id !== statToDelete)
          );
          setSnackbar({
            open: true,
            message: "ICT Statistics deleted successfully!",
            severity: "success",
          });
        }
      } catch (error) {
        setSnackbar({
          open: true,
          message: "Error deleting ICT Statistics",
          severity: "error",
        });
      }
      setOpenDeleteDialog(false);
      setStatToDelete(null);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: { xs: "100%", sm: "100%", md: "100%" },
        margin: "auto",
        p: { xs: 1, sm: 2, md: 3 },
        overflowX: "auto",
      }}
    >
      <div style={{ marginRight: "24px" }}>
        <h1
          style={{
            fontSize: "1.5rem",
            fontWeight: "bold",
            marginBottom: "1rem",
            display: "flex",
            justifyContent: "center",
          }}
        >
          ICT Statistics Dashboard
        </h1>
        <Divider sx={{ marginBottom: 2 }} />

        {error && (
          <Alert severity="error" className="mb-4">
            {error}
          </Alert>
        )}

        <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Project Name</TableCell>
                <TableCell>Output Name</TableCell>
                <TableCell>Indicator</TableCell>
                <TableCell>Baseline Value</TableCell>
                <TableCell>Target Value</TableCell>
                <TableCell>Target Achieved Value</TableCell>
                <TableCell>Cumulative Target Value</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ictStatistics.map((stat) => (
                <TableRow key={stat._id}>
                  <TableCell>
                    {stat.projectName?.projectName || "N/A"}
                  </TableCell>
                  <TableCell>{stat.outputName}</TableCell>
                  <TableCell>{stat.indicator}</TableCell>
                  <TableCell>{stat.baseline}</TableCell>
                  <TableCell>{stat.target}</TableCell>
                  <TableCell>{stat.targetAchieved}</TableCell>
                  <TableCell>{stat.cumulativeTarget}</TableCell>
                  <TableCell>
                    {canEditDelete(stat) && (
                      <>
                        <IconButton
                          onClick={() => handleEdit(stat)}
                          color="primary"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          onClick={() => {
                            setStatToDelete(stat._id);
                            setOpenDeleteDialog(true);
                          }}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      {/* Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Edit ICT Statistics</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Baseline Value"
            value={editFormData.baseline}
            onChange={(e) =>
              setEditFormData({ ...editFormData, baseline: e.target.value })
            }
            margin="normal"
          />
          <TextField
            fullWidth
            label="Target Value"
            value={editFormData.target}
            onChange={(e) =>
              setEditFormData({ ...editFormData, target: e.target.value })
            }
            margin="normal"
          />
          <TextField
            fullWidth
            label="Target Achieved Value"
            value={editFormData.targetAchieved}
            onChange={(e) =>
              setEditFormData({
                ...editFormData,
                targetAchieved: e.target.value,
              })
            }
            margin="normal"
          />
          <TextField
            fullWidth
            label="Cumulative Target Value"
            value={editFormData.cumulativeTarget}
            onChange={(e) =>
              setEditFormData({
                ...editFormData,
                cumulativeTarget: e.target.value,
              })
            }
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleUpdate} color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this ICT Statistics record?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
